import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { MessageCircle, Plus, Clock } from 'lucide-react';
import { getTicketsByCompanyId, createTicket } from '../../services/storage';
import { v4 as uuidv4 } from '../../lib/uuid';
import type { Ticket } from '../../types';

export const ClientSupport = () => {
  const { user, company } = useAuth();
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [tickets, setTickets] = useState<Ticket[]>(
    company ? getTicketsByCompanyId(company.id) : []
  );
  
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    category: 'suporte'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!company || !user) return;

    const ticket: Ticket = {
      id: uuidv4(),
      companyId: company.id,
      userId: user.id,
      title: newTicket.title,
      description: newTicket.description,
      status: 'open',
      priority: 'medium',
      category: newTicket.category,
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0]
    };

    createTicket(ticket);
    setTickets(getTicketsByCompanyId(company.id));
    setNewTicket({ title: '', description: '', category: 'suporte' });
    setShowNewTicket(false);
  };

  const getStatusBadge = (status: Ticket['status']) => {
    const variants = {
      open: 'primary',
      in_progress: 'secondary',
      resolved: 'success',
      closed: 'default'
    } as const;
    
    const labels = {
      open: 'Aberto',
      in_progress: 'Em Andamento',
      resolved: 'Resolvido',
      closed: 'Fechado'
    };

    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  return (
    <DashboardLayout>
      <Helmet>
        <title>Suporte | Portal Ethics</title>
      </Helmet>

      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Suporte</h1>
            <p className="text-neutral-600">Abra tickets e acompanhe seu histórico de suporte</p>
          </div>
          <Button 
            variant="primary" 
            className="flex items-center gap-2"
            onClick={() => setShowNewTicket(true)}
          >
            <Plus className="w-4 h-4" />
            Novo Ticket
          </Button>
        </div>

        {/* New Ticket Form */}
        {showNewTicket && (
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Abrir Novo Ticket</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Categoria
                </label>
                <select
                  value={newTicket.category}
                  onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-neutral-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary-blue/50 focus:border-primary-blue"
                >
                  <option value="suporte">Suporte Técnico</option>
                  <option value="financeiro">Financeiro</option>
                  <option value="configuracao">Configuração</option>
                  <option value="duvida">Dúvida Geral</option>
                  <option value="sugestao">Sugestão</option>
                </select>
              </div>

              <Input
                label="Assunto"
                value={newTicket.title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTicket({ ...newTicket, title: e.target.value })}
                placeholder="Descreva brevemente o assunto"
                required
              />

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Descrição
                </label>
                <Textarea
                  value={newTicket.description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewTicket({ ...newTicket, description: e.target.value })}
                  placeholder="Descreva detalhadamente sua dúvida ou problema..."
                  rows={4}
                  required
                />
              </div>

              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowNewTicket(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" variant="primary">
                  Enviar Ticket
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Tickets List */}
        <div className="space-y-4">
          {tickets.length === 0 ? (
            <Card className="p-12 text-center">
              <MessageCircle className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 mb-2">
                Nenhum ticket encontrado
              </h3>
              <p className="text-neutral-600">
                Você ainda não abriu nenhum ticket de suporte.
              </p>
            </Card>
          ) : (
            tickets.map((ticket) => (
              <Card key={ticket.id} className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-neutral-900">{ticket.title}</h3>
                      {getStatusBadge(ticket.status)}
                    </div>
                    <p className="text-neutral-600 mb-3">{ticket.description}</p>
                    <div className="flex items-center gap-4 text-sm text-neutral-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Criado em {new Date(ticket.createdAt).toLocaleDateString('pt-BR')}
                      </span>
                      <span className="capitalize">Categoria: {ticket.category}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};
